/**
 * Turkish-aware matching for type-to-filter pickers.
 *
 * WHY THIS ISN'T toLowerCase()
 * `"İ".toLowerCase()` returns "i" followed by U+0307 (combining dot above),
 * which never equals "i" — so İncir is unreachable by typing i. Dotless ı has
 * the mirror problem: it lowercases to itself, so Kayısı is unreachable by
 * typing kayisi. Both are the letters people actually type on a phone.
 */

/** Mapped before lowercasing, so İ never becomes i + combining dot. */
const TR_FOLD: Record<string, string> = {
  İ: 'i',
  I: 'i',
  ı: 'i',
  Ş: 's',
  ş: 's',
  Ğ: 'g',
  ğ: 'g',
  Ü: 'u',
  ü: 'u',
  Ö: 'o',
  ö: 'o',
  Ç: 'c',
  ç: 'c',
};

/** Turkish text reduced to the plain ASCII a user types on a phone keyboard. */
export function foldTr(value: string): string {
  return (
    Array.from(value)
      .map((ch) => TR_FOLD[ch] ?? ch)
      .join('')
      .toLowerCase()
      // any non-Turkish diacritic that survived, e.g. a pasted "Café"
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
  );
}

export interface Named {
  name: string;
}

/**
 * Contains-match, prefix matches first.
 *
 * Prefix-first is the whole point: "A" has to surface Armut, which a plain
 * contains-match buries under Elma and Kayısı. Contains still runs so a
 * half-remembered middle ("rpu" → Karpuz) finds its product.
 *
 * Both groups keep the order they arrived in — that is the curated
 * `sort_order` from the backend, and re-sorting alphabetically here would
 * silently throw it away.
 */
export function filterByQuery<T extends Named>(items: readonly T[], query: string): T[] {
  const q = foldTr(query.trim());
  if (!q) return [...items];

  const prefix: T[] = [];
  const contains: T[] = [];

  for (const item of items) {
    const name = foldTr(item.name);
    if (name.startsWith(q)) prefix.push(item);
    else if (name.includes(q)) contains.push(item);
  }

  return [...prefix, ...contains];
}
